import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  MouseEvent,
  ReactNode,
} from "react";
import ReactPlayer from "react-player";
import { FaPause, FaPlay } from "react-icons/fa";
import {
  formatSeconds,
  progressPercentageFromDuration,
  progressPercentageFromHTMLWidth,
  timeFromPercentage,
} from "./utils";
import { cn } from "@jammable/ui-core";
import { MdOutlineRotateRight } from "react-icons/md";
import Link from "next/link";
import { ModelInfo } from "@jammable/clientapi";
import { Spinner } from "../Loader";
import VoiceImage from "../../../../apps/web/components/VoiceImage";
import { KaraokeLyrics } from "./KaraokeLyrics";

export interface AudioPlayerHandle {
  play: () => void;
  stop: () => void;
  getProgress: () => number;
  setProgress: (progress: number) => void;
}

interface VoiceTime {
  start: number; // Start time in seconds
  end: number; // End time in seconds
  voice: string; // Corresponding model's `urlParam`
}
export interface AudioPlayerProps {
  url: string;
  onPlay?: () => void;
  // The song played to the end (the player has rewound to the start).
  onEnded?: () => void;
  models?: ModelInfo[];
  voiceTimes?: VoiceTime[];
  duration?: number;
  className?: string;
  transcription?: any[] | null;
  // Fires with the playhead in seconds while playing, so a page can follow
  // the song (e.g. a lyrics card) without the player rendering the lyrics.
  onTimeUpdate?: (seconds: number) => void;
  clip?: {
    inTime?: number;
    outTime?: number;
    duration?: number;
  };
}

export const AudioPlayerContainer = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "tw-m-auto tw-rounded-3xl tw-p-6",
      "tw-bg-gradient-to-b tw-from-violet-950 tw-to-violet-800",
      className,
    )}
  >
    {children}
  </div>
);

export const SecondaryAudioPlayerContainer = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "tw-rounded-xl tw-border tw-bg-white tw-p-4 tw-text-center tw-shadow tw-backdrop-blur-md",
      className,
    )}
  >
    {children}
  </div>
);

export const AudioPlayer = forwardRef<AudioPlayerHandle, AudioPlayerProps>(
  (
    {
      url,
      onPlay = () => {},
      onEnded,
      models,
      voiceTimes,
      duration,
      className,
      transcription,
      clip,
      onTimeUpdate,
    }: AudioPlayerProps,
    ref,
  ) => {
    const playerRef = useRef<ReactPlayer>(null);
    const [playing, setPlaying] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [allowProgressUpdate, setAllowProgressUpdate] = useState(true);
    const durationFinal = duration
      ? duration
      : playerRef.current?.getDuration() || 0;

    const inTime = clip?.inTime || 0;
    const outTime = clip?.outTime || durationFinal;
    const clipDuration = clip?.duration || durationFinal;

    // Calculate the free/clipped region as a percentage of the full duration
    const freeDuration = clip
      ? ((outTime - inTime) / durationFinal) * 100
      : 100;

    const currentProgresTime = timeFromPercentage(clipDuration, progress);
    const [isLoading, setIsLoading] = useState(true);

    // Calculate the clipped region as a percentage of the full duration
    const clipStartPercent = clip ? (inTime / durationFinal) * 100 : 0;

    useImperativeHandle(ref, () => ({
      play() {
        setPlaying(true);
      },
      stop() {
        setPlaying(false);
      },
      getProgress() {
        return progress;
      },
      setProgress(progress: number) {
        updateProgress(progress);
      },
    }));

    useEffect(() => {
      if (!playerRef?.current || !allowProgressUpdate) return;
      const interval = setInterval(() => {
        if (!playing) return;
        const currentTime = playerRef!.current!.getCurrentTime();

        // Always calculate progress as percentage of the player's duration
        const newProgress = progressPercentageFromDuration(
          currentTime,
          playerRef!.current!.getDuration(),
        );
        setProgress(newProgress);
        setCurrentTime(currentTime);
        onTimeUpdate?.(currentTime);
      }, 200);
      return () => clearInterval(interval);
    }, [playing, allowProgressUpdate, transcription, onTimeUpdate]);

    const togglePlay = () => setPlaying(!playing);

    const updateProgress = (percentage: number) => {
      if (!playerRef.current) return;

      const newTime = timeFromPercentage(
        clip?.duration ?? durationFinal,
        percentage,
      );

      playerRef.current.seekTo(newTime);
      setProgress(percentage);
    };

    const handlePlaybackEnd = () => {
      setPlaying(false);
      setProgress(0);
      playerRef.current!.seekTo(0);
      onEnded?.();
    };

    const trackForwards = () => {
      if (!playerRef.current) return;
      // Ensure progress doesn't exceed 100%
      updateProgress(Math.min(progress + 5, 100));
    };

    const trackBackwards = () => {
      if (!playerRef.current) return;
      // Ensure progress doesn't go below 0%
      updateProgress(Math.max(progress - 5, 0));
    };

    const handleProgressBarClick = (e: MouseEvent<HTMLElement>) => {
      if (!playerRef.current) return;
      setAllowProgressUpdate(false);
      const newProgress = progressPercentageFromHTMLWidth(
        e.currentTarget as HTMLElement,
        e.clientX,
      );
      updateProgress(newProgress);
      setAllowProgressUpdate(true);
    };

    // @TODO make keyboard accessible
    const handleProgressBarKeyDown = () => {};

    // @TODO remove once fixed in react-player
    // forces clientside rendering only due to hydration error
    // see https://github.com/cookpete/react-player/issues/1428
    const [hasWindow, setHasWindow] = useState(false);
    useEffect(() => {
      if (typeof window !== "undefined") {
        setHasWindow(true);
      }
    }, []);
    if (!hasWindow) return <></>;

    return (
      <div className="tw-m-auto tw-min-w-60 tw-text-center tw-font-bold">
        <div
          className={cn(
            "tw-rounded-xl tw-bg-white tw-p-4 tw-text-purple-700",
            className,
          )}
        >
          <div className="tw-flex tw-justify-between">
            <span className="tw-flex tw-w-12">
              {formatSeconds(
                isNaN(currentProgresTime) ? 0 : currentProgresTime + inTime,
              )}
            </span>
            {models &&
              models?.length > 0 &&
              voiceTimes &&
              voiceTimes?.length > 0 && (
                <div className="tw-mt-0 tw-flex tw--space-x-2">
                  {models.map((model) => {
                    const isActive = voiceTimes?.some((voice) => {
                      const startP = (voice.start / clipDuration) * 100;
                      const endP = (voice.end / clipDuration) * 100;
                      return (
                        progress > startP &&
                        progress < endP &&
                        model?.urlParam === voice?.voice
                      );
                    });

                    return (
                      <Link key={model?._id} href={`/${model?.urlParam}`}>
                        <VoiceImage
                          className={`tw-rounded-3xl tw-transition-all ${
                            isActive
                              ? "tw-scale-110 tw-border-3 tw-border-solid tw-border-purple-300 tw-opacity-100"
                              : "tw-border-3 tw-border-solid tw-border-purple-100  tw-opacity-40"
                          }`}
                          model={model}
                          size={35}
                          fill={false}
                          sizes={null}
                          quality={75}
                          priority={false}
                          style={null}
                        />
                      </Link>
                    );
                  })}
                </div>
              )}{" "}
            <span className="tw-flex tw-w-12 tw-justify-end">
              {formatSeconds(durationFinal)}
            </span>
          </div>
          <div
            className={
              "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-py-3 tw-pb-6"
            }
          >
            <div className="tw-relative tw-h-1 tw-w-full tw-rounded-xl tw-bg-gray-100">
              <div
                tabIndex={0}
                onClick={handleProgressBarClick}
                onKeyDown={handleProgressBarKeyDown}
                role="slider"
                aria-valuenow={progress}
                style={{
                  width: `${freeDuration}%`,
                  display: "flex",
                  position: "relative",
                  left: `${clipStartPercent}%`,
                  height: 10,
                }}
              >
                <div className="tw-absolute tw-h-1 tw-w-full tw-rounded-xl tw-bg-purple-100" />
                <div
                  className="tw-absolute tw-h-1 tw-rounded-xl tw-bg-purple-700"
                  style={{
                    width: `${progress}%`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="tw-relative tw-flex tw-w-full">
            {models &&
              models?.length > 1 &&
              voiceTimes &&
              voiceTimes?.length > 0 &&
              voiceTimes?.map((voice) => {
                const model = models.find((m) => m.urlParam === voice.voice);
                const d = ((voice.end - voice.start) / durationFinal) * 100;
                const startP = (voice.start / clipDuration) * 100;
                const endP = (voice.end / clipDuration) * 100;

                const isPlayingNow = progress > startP && progress < endP;

                return (
                  model && (
                    <div
                      style={{
                        backgroundColor: isPlayingNow
                          ? `rgb(${model?.image?.blurhash},1)`
                          : `rgb(${model.image?.blurhash},0.3)`,
                        width: `${d}%`,
                        left: `${clipStartPercent}%`,
                        position: "relative",
                        transition: "all 0.2s ease-in-out",
                        boxShadow: isPlayingNow
                          ? `-0px 0px 16px 2px rgba(${model?.image?.blurhash}, 1)`
                          : undefined,
                        marginInline: 1,
                        borderRadius: 8,
                      }}
                      className="tw-mb-5 tw-h-1 tw-bg-violet-300"
                    ></div>
                  )
                );
              })}
            <div className="tw-absolute tw-h-1 tw-rounded-xl tw-bg-purple-700" />
          </div>
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-6">
            <button
              className="tw-h-6 -tw-scale-x-100 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-purple-700 hover:tw-text-purple-900"
              onClick={trackBackwards}
            >
              <MdOutlineRotateRight size={28} />
            </button>
            <button
              className="tw-h-6 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-purple-700 hover:tw-text-purple-900 "
              onClick={togglePlay}
            >
              {playing ? (
                isLoading ? (
                  <Spinner size={6} />
                ) : (
                  <FaPause size={24} />
                )
              ) : (
                <FaPlay size={24} />
              )}
            </button>
            <button
              className="tw-h-6 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-purple-700 hover:tw-text-purple-900 "
              onClick={trackForwards}
            >
              <MdOutlineRotateRight size={28} />
            </button>
          </div>
          {transcription && (
            <div className="tw-relative tw-flex tw-w-full tw-justify-center">
              <KaraokeLyrics
                transcription={transcription}
                currentTime={currentTime}
                className="tw-mt-5"
              />
            </div>
          )}
          <ReactPlayer
            style={{
              display: "none",
            }}
            onPlay={onPlay}
            onEnded={handlePlaybackEnd}
            ref={playerRef}
            url={url}
            playing={playing}
            onReady={() => setIsLoading(false)}
            onBuffer={() => setIsLoading(true)}
            onStart={() => setIsLoading(false)}
          />
        </div>
      </div>
    );
  },
);

AudioPlayer.displayName = "AudioPlayer";
